import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import HeroHeaderSettings from '@/components/admin/HeroHeaderSettings';
import AdminLayout from '@/components/AdminLayout';
import { DollarSign, FileText, Palette, Save, Play, Mail, Send, Eye, EyeOff, Upload, Image, Code, Trash2, Layout, Plus, X, Users, Database, Download, Loader2, CheckCircle, AlertCircle, RotateCcw, AlertTriangle, History, CalendarClock, Clock } from 'lucide-react';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingLandingPage, setSavingLandingPage] = useState(false);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingLoginImage, setUploadingLoginImage] = useState(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);

  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const loginImageInputRef = useRef(null);
  const heroImageInputRef = useRef(null);

  // General settings
  const [appName, setAppName] = useState('');
  
  // Branding settings
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [loginImageUrl, setLoginImageUrl] = useState('');
  const [footerHtml, setFooterHtml] = useState('');
  const [useLogoInHeader, setUseLogoInHeader] = useState(true);

  // Milestone celebration settings
  const [streakEnabled, setStreakEnabled] = useState(true);
  const [streakMilestones, setStreakMilestones] = useState({});
  const [streakMeta, setStreakMeta] = useState([]);
  const [savingStreak, setSavingStreak] = useState(false);
  
  // Membership fee settings
  const [monthlyFee, setMonthlyFee] = useState(100);
  const [currency, setCurrency] = useState('USD');
  const [membershipPeriod, setMembershipPeriod] = useState(12);
  const [invoiceDay, setInvoiceDay] = useState(1);
  const [gracePeriod, setGracePeriod] = useState(14);
  const [showLivesBreakdown, setShowLivesBreakdown] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [autoGenerateInvoices, setAutoGenerateInvoices] = useState(true);
  const [autoApproveMembers, setAutoApproveMembers] = useState(false);
  const [autoSendReceipts, setAutoSendReceipts] = useState(false);

  // Membership Types
  const [membershipTypes, setMembershipTypes] = useState(['Gold']);
  const [newMembershipType, setNewMembershipType] = useState('');
  const [savingMembershipTypes, setSavingMembershipTypes] = useState(false);

  // SMTP settings
  const [smtpEnabled, setSmtpEnabled] = useState(false);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [encryptionType, setEncryptionType] = useState('tls');
  const [senderName, setSenderName] = useState('MemberHub');
  const [senderEmail, setSenderEmail] = useState('');
  const [testEmailRecipient, setTestEmailRecipient] = useState('');

  // Landing Page Content
  const [heroTitle, setHeroTitle] = useState('');
  const [heroSubtitle, setHeroSubtitle] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [heroButtonText, setHeroButtonText] = useState('');
  const [featuresTitle, setFeaturesTitle] = useState('');
  const [featuresSubtitle, setFeaturesSubtitle] = useState('');
  const [features, setFeatures] = useState([]);
  const [ctaTitle, setCtaTitle] = useState('');
  const [ctaSubtitle, setCtaSubtitle] = useState('');
  const [ctaButtonText, setCtaButtonText] = useState('');

  // Database Backup (Super Admin only)
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupProgress, setBackupProgress] = useState(0);
  const [backupId, setBackupId] = useState(null);
  const [backupHistory, setBackupHistory] = useState([]);
  const backupPollInterval = useRef(null);

  // Automatic backup schedule (Super Admin only)
  const [schedule, setSchedule] = useState({ enabled: false, frequency: 'weekly', interval_days: 7, hour_utc: 2, retention_count: 5, email_confirmation: true, backup_type: 'full', next_run_at: null, last_run_at: null, last_status: null });
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Database Restore (Super Admin only)
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [restoreStatus, setRestoreStatus] = useState(null);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [restoreId, setRestoreId] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreValidation, setRestoreValidation] = useState(null);
  const [validatingRestore, setValidatingRestore] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoreHistoryTarget, setRestoreHistoryTarget] = useState(null);
  const restorePollInterval = useRef(null);
  const restoreFileInputRef = useRef(null);

  useEffect(() => {
    checkAccessAndLoad();
  }, []);

  const checkAccessAndLoad = async () => {
    try {
      // Check if user has super_admin access
      const roleResponse = await apiClient.get('/admin/users/me/role');
      if (roleResponse.data.role !== 'super_admin') {
        toast.error('Access denied. Super Admin privileges required.');
        navigate('/admin');
        return;
      }
      loadSettings();
      loadLandingPageContent();
      loadBackupHistory();
      loadBackupSchedule();
      loadStreakSettings();
    } catch (error) {
      toast.error('Access denied');
      navigate('/admin');
    }
  };

  const loadStreakSettings = async () => {
    try {
      const res = await apiClient.get('/settings/streak');
      setStreakEnabled(res.data.enabled);
      setStreakMilestones(res.data.milestones || {});
      setStreakMeta(res.data.milestone_meta || []);
    } catch (e) {
      // non-blocking
    }
  };

  const handleSaveStreak = async () => {
    setSavingStreak(true);
    try {
      await apiClient.put('/settings/streak', {
        enabled: streakEnabled,
        milestones: streakMilestones,
      });
      toast.success('Milestone celebration settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save milestone settings');
    } finally {
      setSavingStreak(false);
    }
  };

  const setMilestoneField = (key, field, value) => {
    setStreakMilestones((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const loadSettings = async () => {
    try {
      const response = await apiClient.get('/settings/');
      const data = response.data;
      setSettings(data);
      
      // General settings
      setAppName(data.app_name || 'MemberHub');
      
      // Branding settings
      setLogoUrl(data.logo_url || '');
      setFaviconUrl(data.favicon_url || '');
      setLoginImageUrl(data.login_image_url || '');
      setFooterHtml(data.footer_html || '');
      setUseLogoInHeader(data.use_logo_in_header !== false);
      
      // Membership fee settings
      if (data.membership_fee) {
        setMonthlyFee(data.membership_fee.monthly_fee || 100);
        setCurrency(data.membership_fee.currency || 'USD');
        setMembershipPeriod(data.membership_fee.membership_period_months || 12);
        setInvoiceDay(data.membership_fee.invoice_day_of_month || 1);
        setGracePeriod(data.membership_fee.grace_period_days || 14);
        setShowLivesBreakdown(data.membership_fee.show_lives_breakdown || false);
      }
      setAutoGenerateInvoices(data.auto_generate_invoices !== false);
      setAutoApproveMembers(data.auto_approve_members || false);
      setAutoSendReceipts(data.auto_send_receipts || false);
      
      // Membership Types
      if (data.membership_types && data.membership_types.length > 0) {
        setMembershipTypes(data.membership_types);
      }
      
      // SMTP settings
      setSmtpEnabled(data.smtp_enabled || false);
      if (data.smtp_settings) {
        setSmtpHost(data.smtp_settings.smtp_host || '');
        setSmtpPort(data.smtp_settings.smtp_port || 587);
        setSmtpUsername(data.smtp_settings.smtp_username || '');
        setSmtpPassword(data.smtp_settings.smtp_password || '');
        setEncryptionType(data.smtp_settings.encryption_type || 'tls');
        setSenderName(data.smtp_settings.sender_name || 'MemberHub');
        setSenderEmail(data.smtp_settings.sender_email || '');
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadLandingPageContent = async () => {
    try {
      const response = await apiClient.get('/settings/landing-page');
      const data = response.data;
      
      setHeroTitle(data.hero_title || '');
      setHeroSubtitle(data.hero_subtitle || '');
      setHeroImageUrl(data.hero_image_url || '');
      setHeroButtonText(data.hero_button_text || '');
      setFeaturesTitle(data.features_title || '');
      setFeaturesSubtitle(data.features_subtitle || '');
      setFeatures(data.features || []);
      setCtaTitle(data.cta_title || '');
      setCtaSubtitle(data.cta_subtitle || '');
      setCtaButtonText(data.cta_button_text || '');
    } catch (error) {
      // Use defaults
      setFeatures([
        { icon: "Shield", title: "Secure Management", description: "Your membership data is protected with enterprise-grade security" },
        { icon: "Users", title: "Easy Registration", description: "Simple application process with step-by-step guidance" },
        { icon: "FileText", title: "Document Portal", description: "Access your invoices, receipts, and statements anytime" },
        { icon: "CreditCard", title: "Flexible Payments", description: "Multiple payment options with secure verification" },
      ]);
    }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      await apiClient.put('/settings/', {
        app_name: appName,
        logo_url: logoUrl,
        login_image_url: loginImageUrl,
        footer_html: footerHtml,
        use_logo_in_header: useLogoInHeader
      });
      toast.success('Branding settings saved successfully!');
      // Reload to update layout
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save branding settings');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleSaveLandingPage = async () => {
    setSavingLandingPage(true);
    try {
      await apiClient.put('/settings/landing-page', {
        hero_title: heroTitle,
        hero_subtitle: heroSubtitle,
        hero_image_url: heroImageUrl,
        hero_button_text: heroButtonText,
        features_title: featuresTitle,
        features_subtitle: featuresSubtitle,
        features: features,
        cta_title: ctaTitle,
        cta_subtitle: ctaSubtitle,
        cta_button_text: ctaButtonText
      });
      toast.success('Landing page content saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save landing page content');
    } finally {
      setSavingLandingPage(false);
    }
  };

  const addFeature = () => {
    setFeatures([...features, { icon: "Star", title: "", description: "" }]);
  };

  const updateFeature = (index, field, value) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [field]: value };
    setFeatures(updated);
  };

  const removeFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'logo');

      const response = await apiClient.post('/settings/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setLogoUrl(response.data.url);
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/png', 'image/x-icon', 'image/vnd.microsoft.icon', 'image/svg+xml'];
    const nameOk = /\.(png|ico|svg)$/i.test(file.name);
    if (!allowed.includes(file.type) && !nameOk) {
      toast.error('Please select a PNG, ICO, or SVG file');
      return;
    }

    if (file.size > 1 * 1024 * 1024) {
      toast.error('Favicon size must be less than 1MB');
      return;
    }

    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'favicon');

      const response = await apiClient.post('/settings/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFaviconUrl(response.data.url);
      toast.success('Favicon uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleLoginImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingLoginImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'login_image');

      const response = await apiClient.post('/settings/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setLoginImageUrl(response.data.url);
      toast.success('Login image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingLoginImage(false);
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingHeroImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'hero_image');

      const response = await apiClient.post('/settings/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setHeroImageUrl(response.data.url);
      toast.success('Hero image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload hero image');
    } finally {
      setUploadingHeroImage(false);
    }
  };

  const downloadDocumentation = async (audience, title) => {
    setDownloadingDoc(audience);
    try {
      const res = await apiClient.get(`/settings/documentation/${audience}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${title} downloaded`);
    } catch (e) {
      toast.error('Failed to download documentation');
    } finally {
      setDownloadingDoc(null);
    }
  };

  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      await apiClient.put('/settings/', {
        membership_fee: {
          monthly_fee: parseFloat(monthlyFee),
          currency: currency,
          membership_period_months: parseInt(membershipPeriod),
          invoice_day_of_month: parseInt(invoiceDay),
          grace_period_days: parseInt(gracePeriod),
          show_lives_breakdown: showLivesBreakdown
        },
        auto_generate_invoices: autoGenerateInvoices,
        auto_approve_members: autoApproveMembers,
        auto_send_receipts: autoSendReceipts
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMembershipTypes = async () => {
    if (membershipTypes.length === 0) {
      toast.error('At least one membership type is required');
      return;
    }
    
    setSavingMembershipTypes(true);
    try {
      await apiClient.put('/settings/membership-types', {
        membership_types: membershipTypes
      });
      toast.success('Membership types saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save membership types');
    } finally {
      setSavingMembershipTypes(false);
    }
  };

  const addMembershipType = () => {
    const trimmed = newMembershipType.trim();
    if (!trimmed) {
      toast.error('Please enter a membership type name');
      return;
    }
    if (membershipTypes.includes(trimmed)) {
      toast.error('This membership type already exists');
      return;
    }
    setMembershipTypes([...membershipTypes, trimmed]);
    setNewMembershipType('');
  };

  const removeMembershipType = (type) => {
    if (membershipTypes.length <= 1) {
      toast.error('At least one membership type is required');
      return;
    }
    setMembershipTypes(membershipTypes.filter(t => t !== type));
  };

  const handleSaveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await apiClient.put('/settings/', {
        smtp_enabled: smtpEnabled,
        smtp_settings: {
          smtp_host: smtpHost,
          smtp_port: parseInt(smtpPort),
          smtp_username: smtpUsername,
          smtp_password: smtpPassword,
          encryption_type: encryptionType,
          sender_name: senderName,
          sender_email: senderEmail
        }
      });
      toast.success('SMTP settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailRecipient) {
      toast.error('Please enter a recipient email address');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      const response = await apiClient.post('/settings/smtp/test', {
        recipient_email: testEmailRecipient
      });
      toast.success(response.data.message || 'Test email sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleGenerateMonthlyInvoices = async () => {
    setGeneratingInvoices(true);
    try {
      const response = await apiClient.post('/invoices/generate-monthly');
      const data = response.data;
      toast.success(`${data.invoices_generated} invoices generated! (${data.skipped} skipped)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoices');
    } finally {
      setGeneratingInvoices(false);
    }
  };

  // Database Backup Functions
  const initiateBackup = async (backupType = 'full') => {
    setBackupInProgress(true);
    setBackupStatus({ status: 'initiating', message: `Starting ${backupType === 'full' ? 'full' : 'database-only'} backup...` });
    setBackupProgress(0);
    
    try {
      const response = await apiClient.post(`/backup/initiate?backup_type=${backupType}`);
      const { backup_id } = response.data;
      setBackupId(backup_id);
      
      // Start polling for status
      pollBackupStatus(backup_id);
      toast.info('Backup initiated. This may take a few moments...');
    } catch (error) {
      setBackupInProgress(false);
      setBackupStatus({ status: 'failed', message: error.response?.data?.detail || 'Failed to initiate backup' });
      toast.error(error.response?.data?.detail || 'Failed to initiate backup');
    }
  };

  const pollBackupStatus = (id) => {
    // Clear any existing interval
    if (backupPollInterval.current) {
      clearInterval(backupPollInterval.current);
    }
    
    backupPollInterval.current = setInterval(async () => {
      try {
        const response = await apiClient.get(`/backup/status/${id}`);
        const status = response.data;
        
        setBackupStatus(status);
        setBackupProgress(status.progress || 0);
        
        if (status.status === 'completed') {
          clearInterval(backupPollInterval.current);
          toast.success('Backup completed successfully!');
          loadBackupHistory();
        } else if (status.status === 'failed') {
          clearInterval(backupPollInterval.current);
          setBackupInProgress(false);
          toast.error(status.message || 'Backup failed');
        }
      } catch (error) {
        clearInterval(backupPollInterval.current);
        setBackupInProgress(false);
        setBackupStatus({ status: 'failed', message: 'Failed to check backup status' });
      }
    }, 1000);
  };

  const downloadBackup = async () => {
    if (!backupId) return;
    
    try {
      const response = await apiClient.get(`/backup/download/${backupId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backupStatus?.file_name || 'memberhub_backup.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully!');
      loadBackupHistory();
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  const loadBackupHistory = async () => {
    try {
      const res = await apiClient.get('/backup/list');
      const list = (res.data?.backups || []).filter(b => b.status === 'completed');
      setBackupHistory(list);
    } catch (error) {
      console.error('Failed to load backup history:', error);
    }
  };

  const loadBackupSchedule = async () => {
    try {
      const res = await apiClient.get('/backup/schedule');
      setSchedule(res.data);
    } catch (error) {
      console.error('Failed to load backup schedule:', error);
    }
  };

  const saveBackupSchedule = async (overrides = {}) => {
    setSavingSchedule(true);
    try {
      const payload = {
        enabled: schedule.enabled,
        frequency: schedule.frequency,
        interval_days: Number(schedule.interval_days) || 7,
        hour_utc: Number(schedule.hour_utc) || 0,
        retention_count: Number(schedule.retention_count) || 0,
        email_confirmation: schedule.email_confirmation,
        backup_type: schedule.backup_type,
        ...overrides,
      };
      const res = await apiClient.put('/backup/schedule', payload);
      setSchedule(res.data);
      toast.success(res.data.enabled ? 'Automatic backups scheduled' : 'Automatic backups disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
      loadBackupSchedule();
    } finally {
      setSavingSchedule(false);
    }
  };

  const downloadBackupById = async (id, fileName) => {
    try {
      const response = await apiClient.get(`/backup/download/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `memberhub_backup_${id}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  const deleteBackupById = async (id) => {
    try {
      await apiClient.delete(`/backup/cleanup/${id}`);
      toast.success('Backup deleted');
      loadBackupHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete backup');
    }
  };

  const cleanupBackup = async () => {
    // Reset the live backup panel without deleting the persistent backup
    setBackupInProgress(false);
    setBackupStatus(null);
    setBackupId(null);
    setBackupProgress(0);
    loadBackupHistory();
  };

  // Database Restore Functions
  const handleRestoreFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      toast.error('Please select a ZIP file');
      setRestoreFile(null);
      setRestoreValidation(null);
      return;
    }

    if (file.size > 500 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 500MB');
      setRestoreFile(null);
      setRestoreValidation(null);
      return;
    }

    setRestoreFile(file);
    setRestoreValidation(null);
    setValidatingRestore(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/backup/restore/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setRestoreValidation(response.data);
      
      if (response.data.valid) {
        toast.success('Backup file validated successfully!');
      } else {
        toast.error(response.data.message || 'Invalid backup file');
      }
    } catch (error) {
      setRestoreValidation({ valid: false, message: error.response?.data?.detail || 'Validation failed' });
      toast.error(error.response?.data?.detail || 'Failed to validate backup file');
    } finally {
      setValidatingRestore(false);
    }
  };

  const initiateRestore = async () => {
    if (!restoreFile || !restoreValidation?.valid) {
      toast.error('Please select and validate a backup file first');
      return;
    }

    setShowRestoreConfirm(false);
    setRestoreInProgress(true);
    setRestoreStatus({ status: 'initiating', message: 'Starting restore...' });
    setRestoreProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', restoreFile);

      const response = await apiClient.post('/backup/restore/initiate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { restore_id } = response.data;
      setRestoreId(restore_id);

      // Start polling for status
      pollRestoreStatus(restore_id);
      toast.info('Restore initiated. This may take a few moments...');
    } catch (error) {
      setRestoreInProgress(false);
      setRestoreStatus({ status: 'failed', message: error.response?.data?.detail || 'Failed to initiate restore' });
      toast.error(error.response?.data?.detail || 'Failed to initiate restore');
    }
  };

  const restoreFromHistory = async (backupId) => {
    setRestoreHistoryTarget(null);
    setRestoreInProgress(true);
    setRestoreStatus({ status: 'initiating', message: 'Starting restore from stored backup...' });
    setRestoreProgress(0);
    try {
      const response = await apiClient.post(`/backup/restore/from-history/${backupId}`);
      const { restore_id } = response.data;
      setRestoreId(restore_id);
      pollRestoreStatus(restore_id);
      toast.info('Restore initiated from stored backup. This may take a few moments...');
      // scroll the restore panel into view
      document.querySelector('[data-testid="restore-status-panel"]')?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      setRestoreInProgress(false);
      setRestoreStatus({ status: 'failed', message: error.response?.data?.detail || 'Failed to initiate restore' });
      toast.error(error.response?.data?.detail || 'Failed to initiate restore');
    }
  };

  const pollRestoreStatus = (id) => {
    // Clear any existing interval
    if (restorePollInterval.current) {
      clearInterval(restorePollInterval.current);
    }

    restorePollInterval.current = setInterval(async () => {
      try {
        const response = await apiClient.get(`/backup/restore/status/${id}`);
        const status = response.data;

        setRestoreStatus(status);
        setRestoreProgress(status.progress || 0);

        if (status.status === 'completed') {
          clearInterval(restorePollInterval.current);
          toast.success('Database restored successfully! You may need to log in again.');
        } else if (status.status === 'failed') {
          clearInterval(restorePollInterval.current);
          setRestoreInProgress(false);
          toast.error(status.message || 'Restore failed');
        }
      } catch (error) {
        clearInterval(restorePollInterval.current);
        setRestoreInProgress(false);
        setRestoreStatus({ status: 'failed', message: 'Failed to check restore status' });
      }
    }, 1000);
  };

  const resetRestore = () => {
    setRestoreFile(null);
    setRestoreValidation(null);
    setRestoreStatus(null);
    setRestoreProgress(0);
    setRestoreId(null);
    setRestoreInProgress(false);
    setShowRestoreConfirm(false);
    if (restoreFileInputRef.current) {
      restoreFileInputRef.current.value = '';
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (backupPollInterval.current) {
        clearInterval(backupPollInterval.current);
      }
      if (restorePollInterval.current) {
        clearInterval(restorePollInterval.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <AdminLayout currentPage="settings" title="Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="settings" title="Settings">
      <div className="max-w-4xl space-y-6">
        {/* App Branding */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Application Branding
            </CardTitle>
            <CardDescription>Configure your application name, logo, and appearance</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* App Name */}
            <div>
              <Label htmlFor="appName">Application Name</Label>
              <Input
                id="appName"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                placeholder="MemberHub"
                data-testid="app-name-input"
              />
              <p className="text-xs text-muted-foreground mt-1">This name appears in the sidebar and footer</p>
            </div>

            {/* Logo Upload */}
            <div>
              <Label>System Logo</Label>
              <div className="mt-2 flex items-start gap-4">
                <div className="flex-shrink-0">
                  {logoUrl ? (
                    <div className="relative">
                      <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="h-16 w-auto border rounded-lg p-2 bg-white"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 p-1 bg-destructive text-white rounded-full hover:bg-destructive/90"
                        onClick={() => setLogoUrl('')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-16 w-16 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted">
                      <Image className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    data-testid="upload-logo-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Recommended: PNG or SVG, max 2MB. Appears in sidebar and emails.
                  </p>
                </div>
              </div>
            </div>

            {/* Favicon Upload */}
            <div className="border-t pt-4">
              <Label>Browser Favicon</Label>
              <div className="mt-2 flex items-start gap-4">
                <div className="flex-shrink-0">
                  {faviconUrl ? (
                    <div className="relative">
                      <img
                        src={faviconUrl}
                        alt="Favicon"
                        className="h-16 w-16 border rounded-lg p-2 bg-white object-contain"
                        data-testid="favicon-preview"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 p-1 bg-destructive text-white rounded-full hover:bg-destructive/90"
                        onClick={() => setFaviconUrl('')}
                        data-testid="remove-favicon-btn"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-16 w-16 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted">
                      <Image className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept=".png,.ico,.svg,image/png,image/x-icon,image/svg+xml"
                    onChange={handleFaviconUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={uploadingFavicon}
                    data-testid="upload-favicon-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingFavicon ? 'Uploading...' : 'Upload Favicon'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Shows in the browser tab. <strong>Best size: a square 32×32 px</strong> (or 48×48 px) PNG or ICO.
                    A 512×512 px square PNG also works and scales down cleanly. Max 1MB. Save Branding to apply.
                  </p>
                </div>
              </div>
            </div>

            {/* Header Display Option */}
            <div className="border-t pt-4">
              <Label>Header Display</Label>
              <p className="text-xs text-muted-foreground mb-3">Choose what to display in the header/sidebar</p>
              <div className="flex items-center gap-4">
                <div 
                  className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${useLogoInHeader && logoUrl ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
                  onClick={() => setUseLogoInHeader(true)}
                >
                  <input 
                    type="radio" 
                    checked={useLogoInHeader && logoUrl} 
                    onChange={() => setUseLogoInHeader(true)}
                    disabled={!logoUrl}
                  />
                  <div>
                    <p className="text-sm font-medium">Use Logo</p>
                    <p className="text-xs text-muted-foreground">{logoUrl ? 'Show uploaded logo' : 'Upload a logo first'}</p>
                  </div>
                </div>
                <div 
                  className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${!useLogoInHeader || !logoUrl ? 'border-primary bg-primary/5' : 'hover:bg-muted'}`}
                  onClick={() => setUseLogoInHeader(false)}
                >
                  <input 
                    type="radio" 
                    checked={!useLogoInHeader || !logoUrl} 
                    onChange={() => setUseLogoInHeader(false)}
                  />
                  <div>
                    <p className="text-sm font-medium">Use App Name</p>
                    <p className="text-xs text-muted-foreground">Show &quot;{appName}&quot; text</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Login Page Image */}
            <div>
              <Label>Login Page Background Image</Label>
              <div className="mt-2 flex items-start gap-4">
                <div className="flex-shrink-0">
                  {loginImageUrl ? (
                    <div className="relative">
                      <img 
                        src={loginImageUrl} 
                        alt="Login Background" 
                        className="h-20 w-32 object-cover border rounded-lg"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 p-1 bg-destructive text-white rounded-full hover:bg-destructive/90"
                        onClick={() => setLoginImageUrl('')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-20 w-32 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted">
                      <Image className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={loginImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLoginImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => loginImageInputRef.current?.click()}
                    disabled={uploadingLoginImage}
                    data-testid="upload-login-image-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingLoginImage ? 'Uploading...' : 'Upload Image'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Recommended: High-res image (1920x1080), max 5MB. Shown on login/signup pages.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer HTML */}
            <div>
              <Label htmlFor="footerHtml" className="flex items-center gap-2">
                <Code className="w-4 h-4" />
                Custom Footer (HTML)
              </Label>
              <Textarea
                id="footerHtml"
                value={footerHtml}
                onChange={(e) => setFooterHtml(e.target.value)}
                placeholder="<p>© 2026 YourCompany. All rights reserved.</p>"
                rows={4}
                className="font-mono text-sm"
                data-testid="footer-html-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                HTML is supported. Leave empty to use the default footer.
              </p>
              {footerHtml && (
                <div className="mt-3 p-3 border rounded-lg bg-muted">
                  <p className="text-xs font-medium mb-2">Preview:</p>
                  <div 
                    className="text-sm text-center"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(footerHtml) }}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={handleSaveBranding} disabled={savingBranding} data-testid="save-branding-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingBranding ? 'Saving...' : 'Save Branding Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Landing Page Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layout className="w-5 h-5" />
              Landing Page Content
            </CardTitle>
            <CardDescription>Customize the public landing page content shown to visitors</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Hero Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Hero Section</h3>
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="heroTitle">Hero Title</Label>
                  <Input
                    id="heroTitle"
                    value={heroTitle}
                    onChange={(e) => setHeroTitle(e.target.value)}
                    placeholder="Streamline Your Membership Management"
                    data-testid="hero-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="heroSubtitle">Hero Subtitle</Label>
                  <Textarea
                    id="heroSubtitle"
                    value={heroSubtitle}
                    onChange={(e) => setHeroSubtitle(e.target.value)}
                    placeholder="A comprehensive platform for managing memberships..."
                    rows={2}
                    data-testid="hero-subtitle-input"
                  />
                </div>
                <div>
                  <Label htmlFor="heroButtonText">Hero Button Text</Label>
                  <Input
                    id="heroButtonText"
                    value={heroButtonText}
                    onChange={(e) => setHeroButtonText(e.target.value)}
                    placeholder="Get Started"
                    data-testid="hero-button-text-input"
                  />
                </div>
                
                {/* Hero Image Upload */}
                <div>
                  <Label>Hero Image</Label>
                  <p className="text-xs text-muted-foreground mb-2">The main image displayed on the landing page hero section</p>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {heroImageUrl ? (
                        <div className="relative">
                          <img 
                            src={heroImageUrl} 
                            alt="Hero" 
                            className="h-24 w-40 object-cover border rounded-lg"
                          />
                          <button
                            type="button"
                            className="absolute -top-2 -right-2 p-1 bg-destructive text-white rounded-full hover:bg-destructive/90"
                            onClick={() => setHeroImageUrl('')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="h-24 w-40 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted">
                          <Image className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        ref={heroImageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleHeroImageUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => heroImageInputRef.current?.click()}
                        disabled={uploadingHeroImage}
                        data-testid="upload-hero-image-btn"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingHeroImage ? 'Uploading...' : 'Upload Hero Image'}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        Recommended: High-res image (1200x800 or larger), max 5MB. Shown on the landing page.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Features Section</h3>
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="featuresTitle">Features Section Title</Label>
                  <Input
                    id="featuresTitle"
                    value={featuresTitle}
                    onChange={(e) => setFeaturesTitle(e.target.value)}
                    placeholder="Everything You Need"
                    data-testid="features-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="featuresSubtitle">Features Section Subtitle</Label>
                  <Input
                    id="featuresSubtitle"
                    value={featuresSubtitle}
                    onChange={(e) => setFeaturesSubtitle(e.target.value)}
                    placeholder="Powerful features to manage your membership organization"
                    data-testid="features-subtitle-input"
                  />
                </div>
              </div>

              {/* Feature Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Feature Items</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFeature}
                    data-testid="add-feature-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Feature
                  </Button>
                </div>
                
                {features.map((feature, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-muted/50 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">Feature {index + 1}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFeature(index)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`remove-feature-${index}-btn`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div>
                        <Label className="text-xs">Icon Name</Label>
                        <select
                          className="w-full border rounded px-3 py-2 text-sm"
                          value={feature.icon || 'Star'}
                          onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                          data-testid={`feature-${index}-icon-select`}
                        >
                          <option value="Shield">Shield</option>
                          <option value="Users">Users</option>
                          <option value="FileText">FileText</option>
                          <option value="CreditCard">CreditCard</option>
                          <option value="Bell">Bell</option>
                          <option value="Settings">Settings</option>
                          <option value="Star">Star</option>
                          <option value="CheckCircle">CheckCircle</option>
                          <option value="Lock">Lock</option>
                          <option value="Mail">Mail</option>
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Title</Label>
                        <Input
                          value={feature.title || ''}
                          onChange={(e) => updateFeature(index, 'title', e.target.value)}
                          placeholder="Feature title"
                          data-testid={`feature-${index}-title-input`}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Description</Label>
                      <Textarea
                        value={feature.description || ''}
                        onChange={(e) => updateFeature(index, 'description', e.target.value)}
                        placeholder="Feature description"
                        rows={2}
                        data-testid={`feature-${index}-description-input`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Call to Action Section</h3>
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="ctaTitle">CTA Title</Label>
                  <Input
                    id="ctaTitle"
                    value={ctaTitle}
                    onChange={(e) => setCtaTitle(e.target.value)}
                    placeholder="Ready to Get Started?"
                    data-testid="cta-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ctaSubtitle">CTA Subtitle</Label>
                  <Input
                    id="ctaSubtitle"
                    value={ctaSubtitle}
                    onChange={(e) => setCtaSubtitle(e.target.value)}
                    placeholder="Join our membership community today"
                    data-testid="cta-subtitle-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ctaButtonText">CTA Button Text</Label>
                  <Input
                    id="ctaButtonText"
                    value={ctaButtonText}
                    onChange={(e) => setCtaButtonText(e.target.value)}
                    placeholder="Apply Now"
                    data-testid="cta-button-text-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={handleSaveLandingPage} disabled={savingLandingPage} data-testid="save-landing-page-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingLandingPage ? 'Saving...' : 'Save Landing Page Content'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Hero & Header (full-bleed hero + public header actions) */}
        <HeroHeaderSettings branding={{ app_name: appName, logo_url: logoUrl, use_logo_in_header: useLogoInHeader }} />


        {/* Membership Types */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Membership Types
            </CardTitle>
            <CardDescription>Configure available membership types for members</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {membershipTypes.map((type, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg border"
                >
                  <span className="font-medium">{type}</span>
                  <button
                    type="button"
                    onClick={() => removeMembershipType(type)}
                    className="p-1 hover:bg-destructive/20 rounded text-destructive"
                    data-testid={`remove-type-${index}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Input
                placeholder="New membership type name"
                value={newMembershipType}
                onChange={(e) => setNewMembershipType(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addMembershipType()}
                data-testid="new-membership-type-input"
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={addMembershipType} data-testid="add-membership-type-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Type
              </Button>
            </div>
            
            <div className="flex justify-end pt-2">
              <Button onClick={handleSaveMembershipTypes} disabled={savingMembershipTypes} data-testid="save-membership-types-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingMembershipTypes ? 'Saving...' : 'Save Membership Types'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Membership Fee Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Membership Fee Settings
            </CardTitle>
            <CardDescription>Configure monthly membership fees and billing (per-life pricing)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Per-Life Billing:</strong> Members are charged per life (primary member + beneficiaries).
              <br />
              <span className="text-blue-600">Total Monthly = Base Fee × (1 + Number of Beneficiaries)</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="monthlyFee">Base Monthly Fee (per life)</Label>
                <div className="flex gap-2">
                  <Input
                    id="monthlyFee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={monthlyFee}
                    onChange={(e) => setMonthlyFee(e.target.value)}
                    data-testid="monthly-fee-input"
                  />
                  <select 
                    className="border rounded px-3 py-2 w-24"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    data-testid="currency-select"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="ZMW">ZMW</option>
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor="membershipPeriod">Membership Period (months)</Label>
                <Input
                  id="membershipPeriod"
                  type="number"
                  min="1"
                  max="60"
                  value={membershipPeriod}
                  onChange={(e) => setMembershipPeriod(e.target.value)}
                  data-testid="membership-period-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="invoiceDay">Invoice Generation Day</Label>
                <Input
                  id="invoiceDay"
                  type="number"
                  min="1"
                  max="28"
                  value={invoiceDay}
                  onChange={(e) => setInvoiceDay(e.target.value)}
                  data-testid="invoice-day-input"
                />
              </div>
              <div>
                <Label htmlFor="gracePeriod">Grace Period (days)</Label>
                <Input
                  id="gracePeriod"
                  type="number"
                  min="0"
                  max="60"
                  value={gracePeriod}
                  onChange={(e) => setGracePeriod(e.target.value)}
                  data-testid="grace-period-input"
                />
              </div>
            </div>

            <div className="flex items-start justify-between gap-4 border rounded-lg p-4">
              <div className="space-y-1">
                <Label htmlFor="showLivesBreakdown" className="text-base">Show lives breakdown on invoices &amp; statements</Label>
                <p className="text-sm text-muted-foreground">
                  When on, invoice and statement PDFs (and the member's invoice list) show the contribution
                  per life, the lives covered, the period billed and how the amount was reached. Off by default.
                </p>
              </div>
              <Switch
                id="showLivesBreakdown"
                checked={showLivesBreakdown}
                onCheckedChange={setShowLivesBreakdown}
                data-testid="show-lives-breakdown-switch"
              />
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-medium mb-2">Annual Membership Cost</h4>
              <p className="text-2xl font-bold text-primary">
                {currency} {(parseFloat(monthlyFee || 0) * parseInt(membershipPeriod || 12)).toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* System Documentation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              System Documentation
            </CardTitle>
            <CardDescription>
              Role-specific user guides, generated from the live system. Visible to super administrators only.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { id: 'member', title: 'Member Guide', desc: 'Everything a member can do: dashboard, payments, beneficiaries, providers, documents and profile.' },
              { id: 'admin', title: 'Administrator Guide', desc: 'Managing members, approvals, payments, invoices, providers, documents and reports.' },
              { id: 'super_admin', title: 'Super Administrator Guide', desc: 'Settings, users, analytics, backups, data safety and permissions.' },
            ].map((doc) => (
              <div key={doc.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border rounded-lg p-4">
                <div>
                  <p className="font-medium">{doc.title}</p>
                  <p className="text-sm text-muted-foreground">{doc.desc}</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => downloadDocumentation(doc.id, doc.title)}
                  disabled={downloadingDoc === doc.id}
                  data-testid={`download-doc-${doc.id}`}
                  className="shrink-0"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingDoc === doc.id ? 'Preparing…' : 'Download PDF'}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* SMTP Email Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Settings (SMTP)
            </CardTitle>
            <CardDescription>Configure SMTP settings for system emails</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <Label>Enable Email Sending</Label>
                <p className="text-sm text-muted-foreground">
                  Turn on to enable system emails (notifications, invoices, reminders)
                </p>
              </div>
              <Switch
                checked={smtpEnabled}
                onCheckedChange={setSmtpEnabled}
                data-testid="smtp-enabled-switch"
              />
            </div>

            <div className={`space-y-4 ${!smtpEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="smtpHost">SMTP Host *</Label>
                  <Input
                    id="smtpHost"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="smtp.gmail.com"
                    data-testid="smtp-host-input"
                  />
                </div>
                <div>
                  <Label htmlFor="smtpPort">SMTP Port *</Label>
                  <Input
                    id="smtpPort"
                    type="number"
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(e.target.value)}
                    placeholder="587"
                    data-testid="smtp-port-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="smtpUsername">SMTP Username</Label>
                  <Input
                    id="smtpUsername"
                    value={smtpUsername}
                    onChange={(e) => setSmtpUsername(e.target.value)}
                    placeholder="your-email@gmail.com"
                    data-testid="smtp-username-input"
                  />
                </div>
                <div>
                  <Label htmlFor="smtpPassword">SMTP Password</Label>
                  <div className="relative">
                    <Input
                      id="smtpPassword"
                      type={showPassword ? "text" : "password"}
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                      placeholder="••••••••"
                      data-testid="smtp-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Password is encrypted at rest</p>
                </div>
              </div>

              <div>
                <Label htmlFor="encryptionType">Encryption Type</Label>
                <select
                  id="encryptionType"
                  className="w-full border rounded p-2"
                  value={encryptionType}
                  onChange={(e) => setEncryptionType(e.target.value)}
                  data-testid="encryption-type-select"
                >
                  <option value="none">None</option>
                  <option value="tls">TLS (Port 587)</option>
                  <option value="ssl">SSL (Port 465)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="senderName">Sender Name *</Label>
                  <Input
                    id="senderName"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="MemberHub"
                    data-testid="sender-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="senderEmail">Sender Email Address *</Label>
                  <Input
                    id="senderEmail"
                    type="email"
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                    placeholder="noreply@yourdomain.com"
                    data-testid="sender-email-input"
                  />
                </div>
              </div>

              {/* Test Email Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Send Test Email</h4>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testEmailRecipient}
                    onChange={(e) => setTestEmailRecipient(e.target.value)}
                    placeholder="test@example.com"
                    className="flex-1"
                    data-testid="test-email-input"
                  />
                  <Button
                    onClick={handleSendTestEmail}
                    disabled={sendingTestEmail || !smtpEnabled}
                    variant="secondary"
                    data-testid="send-test-email-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingTestEmail ? 'Sending...' : 'Send Test'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Send a test email to verify your SMTP configuration is working correctly.
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={handleSaveSmtp} disabled={savingSmtp} data-testid="save-smtp-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingSmtp ? 'Saving...' : 'Save Email Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Automation Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Automation Settings</CardTitle>
            <CardDescription>Configure automatic processes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Generate Monthly Invoices</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically create invoices for active members each month
                </p>
              </div>
              <Switch
                checked={autoGenerateInvoices}
                onCheckedChange={setAutoGenerateInvoices}
                data-testid="auto-invoice-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Approve Applications</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically approve new membership applications
                </p>
              </div>
              <Switch
                checked={autoApproveMembers}
                onCheckedChange={setAutoApproveMembers}
                data-testid="auto-approve-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Send Payment Receipts</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically send receipt emails when payments are verified
                </p>
              </div>
              <Switch
                checked={autoSendReceipts}
                onCheckedChange={setAutoSendReceipts}
                data-testid="auto-send-receipts-switch"
              />
            </div>
            {autoSendReceipts && !smtpEnabled && (
              <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg text-sm">
                ⚠️ SMTP is not enabled. Auto-send receipts requires email to be configured.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Milestone Celebrations */}
        <Card data-testid="streak-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Payment Milestone Celebrations
            </CardTitle>
            <CardDescription>
              Reward members as they progress on their payment journey with a celebratory
              (confetti on 100%) in-app toast and an optional appreciation email.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <Label>Enable Milestone Celebrations</Label>
                <p className="text-sm text-muted-foreground">Master switch for all milestone toasts and emails</p>
              </div>
              <Switch
                checked={streakEnabled}
                onCheckedChange={setStreakEnabled}
                data-testid="streak-enabled-switch"
              />
            </div>

            <div className={streakEnabled ? '' : 'opacity-50 pointer-events-none'}>
              <div className="hidden sm:grid grid-cols-12 gap-2 px-1 pb-2 text-xs font-medium text-muted-foreground">
                <div className="col-span-6">Milestone</div>
                <div className="col-span-3 text-center">Active</div>
                <div className="col-span-3 text-center">Send Email</div>
              </div>
              <div className="space-y-2">
                {streakMeta.map((m) => {
                  const cfg = streakMilestones[m.key] || {};
                  return (
                    <div key={m.key} className="grid grid-cols-12 gap-2 items-center rounded-lg border p-3">
                      <div className="col-span-12 sm:col-span-6 flex items-center gap-2">
                        <span className="font-medium">{m.label}</span>
                        {m.confetti && (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">🎉 Confetti</Badge>
                        )}
                      </div>
                      <div className="col-span-6 sm:col-span-3 flex sm:justify-center items-center gap-2">
                        <span className="sm:hidden text-sm text-muted-foreground">Active</span>
                        <Switch
                          checked={!!cfg.enabled}
                          onCheckedChange={(v) => setMilestoneField(m.key, 'enabled', v)}
                          data-testid={`milestone-${m.key}-enabled`}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-3 flex sm:justify-center items-center gap-2">
                        <span className="sm:hidden text-sm text-muted-foreground">Email</span>
                        <Switch
                          checked={!!cfg.email_enabled}
                          disabled={!cfg.enabled}
                          onCheckedChange={(v) => setMilestoneField(m.key, 'email_enabled', v)}
                          data-testid={`milestone-${m.key}-email`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {Object.values(streakMilestones).some((c) => c?.email_enabled) && !smtpEnabled && (
                <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg text-sm mt-3">
                  ⚠️ SMTP is not enabled. Appreciation emails will be skipped until email is configured (toasts still show).
                </div>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={handleSaveStreak} disabled={savingStreak} data-testid="save-streak-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingStreak ? 'Saving...' : 'Save Celebration Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Generation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Invoice Generation
            </CardTitle>
            <CardDescription>Manually generate monthly invoices for all active members</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Generate Monthly Invoices</p>
                <p className="text-sm text-muted-foreground">
                  Creates invoices for all active members based on the membership fee settings above.
                </p>
              </div>
              <Button 
                onClick={handleGenerateMonthlyInvoices} 
                disabled={generatingInvoices}
                variant="secondary"
                data-testid="generate-monthly-invoices-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {generatingInvoices ? 'Generating...' : 'Generate Invoices'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Database Backup - Super Admin Only */}
        <Card className="border-2 border-dashed border-slate-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Database Backup
            </CardTitle>
            <CardDescription>
              Create a full backup of your database for disaster recovery or migration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> This creates a complete backup of all your data including members, 
                payments, invoices, and settings. The backup file can be used to restore your data on another 
                MongoDB server.
              </p>
            </div>

            {/* Backup Status */}
            {backupStatus && (
              <div className={`rounded-lg p-4 ${
                backupStatus.status === 'completed' ? 'bg-green-50 border border-green-200' :
                backupStatus.status === 'failed' ? 'bg-red-50 border border-red-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center gap-3">
                  {backupStatus.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : backupStatus.status === 'failed' ? (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  ) : (
                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${
                      backupStatus.status === 'completed' ? 'text-green-800' :
                      backupStatus.status === 'failed' ? 'text-red-800' :
                      'text-blue-800'
                    }`}>
                      {backupStatus.message}
                    </p>
                    {backupStatus.status !== 'completed' && backupStatus.status !== 'failed' && (
                      <Progress value={backupProgress} className="mt-2 h-2" />
                    )}
                    {backupStatus.status === 'completed' && backupStatus.file_size && (
                      <p className="text-xs text-green-600 mt-1">
                        File size: {(backupStatus.file_size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Backup Actions */}
            <div className="flex flex-wrap gap-3">
              {!backupStatus || backupStatus.status === 'failed' ? (
                <>
                  <Button 
                    onClick={() => initiateBackup('full')} 
                    disabled={backupInProgress}
                    className="bg-slate-800 hover:bg-slate-700"
                    data-testid="initiate-backup-btn"
                  >
                    {backupInProgress ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Backup...
                      </>
                    ) : (
                      <>
                        <Database className="w-4 h-4 mr-2" />
                        Full Backup (DB + Files)
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={() => initiateBackup('database')} 
                    disabled={backupInProgress}
                    variant="outline"
                    data-testid="initiate-db-backup-btn"
                  >
                    <Database className="w-4 h-4 mr-2" />
                    Database Only
                  </Button>
                </>
              ) : backupStatus.status === 'completed' ? (
                <>
                  <Button 
                    onClick={downloadBackup}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="download-backup-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Backup
                  </Button>
                  <Button 
                    onClick={cleanupBackup}
                    variant="outline"
                    data-testid="cleanup-backup-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Discard
                  </Button>
                </>
              ) : null}
            </div>

            <div className="text-xs text-muted-foreground">
              <p>The full backup includes:</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>All member records, profiles and beneficiaries</li>
                <li>Payment history, invoices, receipts and reconciliations</li>
                <li>All uploaded files (documents, profile photos, proof of payment) via GridFS</li>
                <li>Filesystem uploads directory (branding logos and any on-disk files)</li>
                <li>System settings, branding, landing page and automation settings</li>
                <li>User accounts (passwords remain encrypted)</li>
                <li>Restoration guide with instructions</li>
              </ul>
            </div>

            {/* Automatic Backup Schedule */}
            <div className="pt-2 border-t" data-testid="backup-schedule">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-slate-600" />
                  <h4 className="text-sm font-semibold">Automatic Backup Schedule</h4>
                </div>
                <Switch
                  checked={schedule.enabled}
                  disabled={savingSchedule}
                  onCheckedChange={(v) => { setSchedule(s => ({ ...s, enabled: v })); saveBackupSchedule({ enabled: v }); }}
                  data-testid="backup-schedule-toggle"
                />
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Automatically create a full GridFS backup on a recurring schedule. Backups appear in the history below and are downloadable at any time.
              </p>

              {schedule.enabled && (
                <div className="space-y-3 bg-slate-50 border rounded-lg p-4" data-testid="backup-schedule-config">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Backup Type</Label>
                      <Select value={schedule.backup_type} onValueChange={(v) => setSchedule(s => ({ ...s, backup_type: v }))}>
                        <SelectTrigger data-testid="backup-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">Full (DB + Files)</SelectItem>
                          <SelectItem value="database">Database Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Frequency</Label>
                      <Select value={schedule.frequency} onValueChange={(v) => setSchedule(s => ({ ...s, frequency: v }))}>
                        <SelectTrigger data-testid="backup-frequency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly (every 7 days)</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="custom">Custom (every X days)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {schedule.frequency === 'custom' && (
                      <div>
                        <Label className="text-xs">Every (days)</Label>
                        <Input
                          type="number" min="1"
                          value={schedule.interval_days}
                          onChange={(e) => setSchedule(s => ({ ...s, interval_days: e.target.value }))}
                          data-testid="backup-interval-input"
                        />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> Run at (UTC hour)</Label>
                      <Select value={String(schedule.hour_utc)} onValueChange={(v) => setSchedule(s => ({ ...s, hour_utc: Number(v) }))}>
                        <SelectTrigger data-testid="backup-hour-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-60">
                          {Array.from({ length: 24 }, (_, h) => (
                            <SelectItem key={h} value={String(h)}>{String(h).padStart(2, '0')}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Keep last (backups)</Label>
                      <Input
                        type="number" min="0"
                        value={schedule.retention_count}
                        onChange={(e) => setSchedule(s => ({ ...s, retention_count: e.target.value }))}
                        data-testid="backup-retention-input"
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">Older auto-backups are pruned. 0 = keep all.</p>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Switch
                      checked={schedule.email_confirmation}
                      onCheckedChange={(v) => setSchedule(s => ({ ...s, email_confirmation: v }))}
                      data-testid="backup-email-toggle"
                    />
                    Email super-admins a confirmation after each scheduled backup (requires SMTP enabled)
                  </label>

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      {schedule.next_run_at && (
                        <p data-testid="backup-next-run">Next run: <span className="font-medium text-slate-700">{new Date(schedule.next_run_at).toLocaleString()}</span></p>
                      )}
                      {schedule.last_run_at && (
                        <p data-testid="backup-last-run">Last run: {new Date(schedule.last_run_at).toLocaleString()} ({schedule.last_status || 'n/a'})</p>
                      )}
                    </div>
                    <Button size="sm" onClick={() => saveBackupSchedule()} disabled={savingSchedule} data-testid="save-backup-schedule-btn">
                      {savingSchedule ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Schedule
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Backup History */}
            <div className="pt-2" data-testid="backup-history">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Backup History</h4>
                <Button variant="ghost" size="sm" onClick={loadBackupHistory} data-testid="refresh-backup-history-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  Refresh
                </Button>
              </div>
              {backupHistory.length === 0 ? (
                <p className="text-xs text-muted-foreground">No backups yet.</p>
              ) : (
                <div className="space-y-2">
                  {restoreHistoryTarget && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="restore-history-confirm">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-amber-800">Restore from stored backup?</p>
                          <p className="text-sm text-amber-700 mt-1">
                            This will <strong>REPLACE ALL CURRENT DATA</strong> with the backup
                            <strong> {restoreHistoryTarget.file_name || restoreHistoryTarget.backup_id}</strong>
                            {restoreHistoryTarget.completed_at && ` (${new Date(restoreHistoryTarget.completed_at).toLocaleString()})`}.
                            A safety backup of the current data is created automatically first. No file upload needed.
                          </p>
                          <div className="flex gap-2 mt-3">
                            <Button
                              className="bg-red-600 hover:bg-red-700 text-white"
                              onClick={() => restoreFromHistory(restoreHistoryTarget.backup_id)}
                              data-testid="confirm-restore-history-btn"
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Yes, Restore This Backup
                            </Button>
                            <Button variant="outline" onClick={() => setRestoreHistoryTarget(null)} data-testid="cancel-restore-history-btn">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {backupHistory.map((b) => (
                    <div
                      key={b.backup_id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
                      data-testid={`backup-item-${b.backup_id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate flex items-center gap-2">
                          {b.file_name || b.backup_id}
                          <Badge variant="outline" className={b.backup_type === 'database' ? 'text-blue-700 border-blue-300 bg-blue-50' : 'text-slate-700 border-slate-300 bg-slate-50'}>
                            {b.backup_type === 'database' ? 'DB Only' : 'Full'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {b.completed_at ? new Date(b.completed_at).toLocaleString() : '—'}
                          {b.file_size != null && ` · ${(b.file_size / (1024 * 1024)).toFixed(2)} MB`}
                          {b.total_documents != null && ` · ${b.total_documents} docs`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          By {b.initiated_by_name || b.initiated_by_email || 'unknown'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          disabled={restoreInProgress}
                          onClick={() => setRestoreHistoryTarget(b)}
                          data-testid={`restore-backup-${b.backup_id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadBackupById(b.backup_id, b.file_name)}
                          data-testid={`download-backup-${b.backup_id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => deleteBackupById(b.backup_id)}
                          data-testid={`delete-backup-${b.backup_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Database Restore - Super Admin Only */}
        <Card className="border-2 border-dashed border-red-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              Database Restore
            </CardTitle>
            <CardDescription>
              Restore your database from a previously created backup file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Critical Warning */}
            <div className="bg-red-50 border border-red-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-800">⚠️ CRITICAL WARNING</p>
                  <p className="text-sm text-red-700 mt-1">
                    This action will <strong>REPLACE ALL CURRENT DATA</strong> with the backup data. 
                    A safety backup of the current database will be created automatically before restoration.
                    This operation cannot be undone easily.
                  </p>
                </div>
              </div>
            </div>

            {/* File Upload Section */}
            <div className="space-y-3">
              <Label>Select Backup File (.zip)</Label>
              <div className="flex gap-3">
                <input
                  ref={restoreFileInputRef}
                  type="file"
                  accept=".zip"
                  onChange={handleRestoreFileSelect}
                  disabled={restoreInProgress}
                  className="flex-1 border rounded-lg px-3 py-2 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                  data-testid="restore-file-input"
                />
                {(restoreFile || restoreValidation || restoreStatus) && (
                  <Button
                    variant="outline"
                    onClick={resetRestore}
                    disabled={restoreInProgress && restoreStatus?.status !== 'completed' && restoreStatus?.status !== 'failed'}
                    data-testid="reset-restore-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {restoreFile && (
                <p className="text-xs text-muted-foreground">
                  Selected: {restoreFile.name} ({(restoreFile.size / (1024 * 1024)).toFixed(2)} MB)
                </p>
              )}
            </div>

            {/* Validation Status */}
            {validatingRestore && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  <p className="text-sm text-blue-800">Validating backup file...</p>
                </div>
              </div>
            )}

            {restoreValidation && !validatingRestore && (
              <div className={`rounded-lg p-4 ${
                restoreValidation.valid 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {restoreValidation.valid ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${
                      restoreValidation.valid ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {restoreValidation.valid ? 'Backup file is valid' : restoreValidation.message}
                    </p>
                    
                    {restoreValidation.valid && restoreValidation.backup_info && (
                      <div className="mt-3 text-xs text-green-700 space-y-1">
                        <p><strong>Database:</strong> {restoreValidation.backup_info.database_name}</p>
                        <p><strong>Created:</strong> {new Date(restoreValidation.backup_info.created_at).toLocaleString()}</p>
                        <p><strong>Collections:</strong> {restoreValidation.backup_info.total_collections}</p>
                        <p><strong>Total Documents:</strong> {restoreValidation.backup_info.total_documents?.toLocaleString()}</p>
                        <div className="mt-2">
                          <p className="font-medium">Included collections:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {restoreValidation.backup_info.collections?.map((col, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-green-200 rounded text-xs">
                                {col}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Restore Progress Status */}
            {restoreStatus && (
              <div data-testid="restore-status-panel" className={`rounded-lg p-4 ${
                restoreStatus.status === 'completed' ? 'bg-green-50 border border-green-200' :
                restoreStatus.status === 'failed' ? 'bg-red-50 border border-red-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center gap-3">
                  {restoreStatus.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : restoreStatus.status === 'failed' ? (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  ) : (
                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${
                      restoreStatus.status === 'completed' ? 'text-green-800' :
                      restoreStatus.status === 'failed' ? 'text-red-800' :
                      'text-blue-800'
                    }`}>
                      {restoreStatus.message}
                    </p>
                    {restoreStatus.status !== 'completed' && restoreStatus.status !== 'failed' && (
                      <Progress value={restoreProgress} className="mt-2 h-2" />
                    )}
                    {restoreStatus.status === 'completed' && restoreStatus.stats && (
                      <div className="mt-2 text-xs text-green-700">
                        <p>Collections restored: {restoreStatus.stats.collections_restored}</p>
                        <p>Documents restored: {restoreStatus.stats.total_documents?.toLocaleString()}</p>
                        <p>Indexes recreated: {restoreStatus.stats.total_indexes}</p>
                        {restoreStatus.stats.failed_collections?.length > 0 && (
                          <p className="text-amber-600">
                            Failed collections: {restoreStatus.stats.failed_collections.join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation Dialog */}
            {showRestoreConfirm && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">Confirm Database Restore</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Are you absolutely sure you want to restore this backup? 
                      All current data will be replaced.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={initiateRestore}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="confirm-restore-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Yes, Restore Database
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowRestoreConfirm(false)}
                        data-testid="cancel-restore-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Restore Action Button */}
            {!showRestoreConfirm && restoreValidation?.valid && !restoreInProgress && restoreStatus?.status !== 'completed' && (
              <Button
                onClick={() => setShowRestoreConfirm(true)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="initiate-restore-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Restore Database
              </Button>
            )}

            {/* Reset after completion */}
            {(restoreStatus?.status === 'completed' || restoreStatus?.status === 'failed') && (
              <Button
                variant="outline"
                onClick={resetRestore}
                data-testid="new-restore-btn"
              >
                <History className="w-4 h-4 mr-2" />
                Start New Restore
              </Button>
            )}

            <div className="text-xs text-muted-foreground border-t pt-4 mt-4">
              <p className="font-medium">Important notes:</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>Only use backups generated by this system</li>
                <li>A pre-restore backup is automatically created for safety</li>
                <li>You may need to log in again after restore completes</li>
                <li>File uploads (images, documents) ARE included via GridFS storage</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Save General Settings Button */}
        <div className="flex justify-end">
          <Button onClick={handleSaveGeneral} disabled={saving} data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save General Settings'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
